// Chat-style AI modify panel for the Title Studio.
//
// Replaces the previous single-shot "type a request → apply" flow with a
// running conversation the operator can scroll through. Each turn is
// preserved locally so refresh doesn't lose context.
//
// Turn model:
//   { role: 'user',      content: string }
//   { role: 'assistant', content: string,   specSnapshot: TitleSpec,
//                        error?: string }
//
// Assistant turns embed the FULL resulting spec so "revert to this
// state" is instant — no LLM round-trip. The chat's user-turn history
// (roles + content only, no snapshots) is passed to the backend so the
// LLM has multi-turn continuity.
//
// Persistence: localStorage per (brandId, format). Server-side history
// is a follow-up if operators start working across devices; today's
// workflow is single-operator single-browser.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, HStack, VStack, Text, Textarea, Button, IconButton, Badge, Spinner, useToast,
} from '@chakra-ui/react';
import { apiJson } from '../auth/apiFetch';

// The payload the chat operates on is generic — Title Studio uses a
// title-spec object; Video Script uses a JS string. The panel treats it
// as opaque via a type parameter and defers to the caller for building
// the request body and extracting the new state from the poll response.
type ChatTurn<TState = unknown> =
  | { role: 'user';      content: string;                                 timestamp: number }
  | { role: 'assistant'; content: string; snapshot?: TState;              error?: string; timestamp: number };

type ModifyKickResponse = { jobId: string };
type ModifyStatusResponse = {
  status: 'pending' | 'done' | 'failed';
  summary?: string | null;
  error?: string;
  attempts?: number;
  // Rest of the shape (spec / script / etc.) varies per caller — the
  // `extractResult` prop reads it.
  [k: string]: unknown;
};

type LlmHistory = Array<{ role: 'user' | 'assistant'; content: string }>;

type Props<TState> = {
  // Endpoints — passed in so the panel works for any scope (brand /
  // product / ad) that exposes a POST-and-poll modify pair.
  modifyUrl:       string;
  modifyStatusUrl: (jobId: string) => string;
  // Storage isolation. Full localStorage key is derived from this; the
  // caller composes it (e.g. `titleStudio.brand.<id>.<format>`).
  storageKey:      string;
  // Current state the operator sees + writes back to. On successful
  // send, the panel pins the snapshot to the assistant turn and calls
  // onStateApplied so the parent re-renders whatever depends on it.
  currentState:    TState | null;
  onStateApplied:  (state: TState) => void;
  // Build the POST body from the composed pieces. Callers pack request
  // text + history + baseState (the current live state) into whatever
  // shape their backend expects (mode/format/direction/baseScript for
  // Video Script; format/history/baseSpec for Title Studio).
  buildRequestBody: (args: { request: string; history: LlmHistory; baseState: TState | null }) => unknown;
  // Extract the new state + human-readable summary from the poll's
  // done response. Returning state=null when there's no state field
  // (e.g. an error we still want to surface as text) is legal.
  extractResult:    (response: ModifyStatusResponse) => { state: TState | null; summary: string | null };
  // UX strings — caller-controlled so both panels can speak the right
  // language ("spec" vs "script").
  placeholder?:      string;
  emptyStateExample?: string;
};

const MAX_TURNS_STORED = 40;    // localStorage size safety
const MAX_TURNS_TO_LLM = 10;    // matches backend cap
const INTERVAL_MS = 1500;
const TIMEOUT_MS  = 2 * 60 * 1000;

export function AIChatPanel<TState>({
  modifyUrl, modifyStatusUrl, storageKey, currentState, onStateApplied,
  buildRequestBody, extractResult,
  placeholder = 'Describe the change… (Shift+Enter for newline)',
  emptyStateExample = 'e.g. "cascade the badges in a row over 0.6s, then show the product image at 5s"',
}: Props<TState>) {
  const toast = useToast();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef  = useRef<HTMLTextAreaElement | null>(null);

  const [history, setHistory] = useState<ChatTurn<TState>[]>(() => loadHistory<TState>(storageKey));
  const [input, setInput]     = useState('');
  const [busy,  setBusy]      = useState(false);

  // Mirror the parent-controlled callbacks into refs. The parent recreates
  // buildRequestBody / extractResult / onStateApplied every render, and
  // those inline lambdas typically close over the parent's *format* (Title
  // Studio) or *mode* (Video Script). If pollJob / send captured them by
  // value at mount time, an AI edit issued on the 4:5 tab would apply its
  // result to whichever format was active AT MOUNT (usually 9:16). The
  // refs are updated in a layout effect so the very next call inside a
  // running poll sees the freshest closures.
  const buildRequestBodyRef = useRef(buildRequestBody);
  const extractResultRef    = useRef(extractResult);
  const onStateAppliedRef   = useRef(onStateApplied);
  useEffect(() => {
    buildRequestBodyRef.current = buildRequestBody;
    extractResultRef.current    = extractResult;
    onStateAppliedRef.current   = onStateApplied;
  });

  // Reload the conversation when the storage key changes (brand / format
  // switch). Persist on every history mutation.
  useEffect(() => { setHistory(loadHistory(storageKey)); }, [storageKey]);
  useEffect(() => { saveHistory(storageKey, history); }, [storageKey, history]);

  // Auto-scroll to the newest turn whenever history grows.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [history.length, busy]);

  // The user-facing send: append a user turn, kick off /modify with the
  // trimmed chat history (LLM only sees text — snapshots stay client-
  // side), poll for result, append assistant turn on done.
  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setBusy(true);

    const userTurn: ChatTurn<TState> = { role: 'user', content: text, timestamp: Date.now() };
    setHistory(prev => trimForStorage([...prev, userTurn]));

    // Compact prior turns for the LLM: role + content only, capped at
    // MAX_TURNS_TO_LLM most recent. The just-appended user turn is
    // conveyed via the `request` field; history is prior context.
    const llmHistory: LlmHistory = history
      .slice(-MAX_TURNS_TO_LLM)
      .map(t => ({ role: t.role, content: t.content }));

    try {
      const body = buildRequestBodyRef.current({ request: text, history: llmHistory, baseState: currentState });
      const kick = await apiJson<ModifyKickResponse>(modifyUrl, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      await pollJob(kick.jobId);
    } catch (err) {
      const msg = (err as Error).message || 'network error';
      setHistory(prev => trimForStorage([...prev, {
        role: 'assistant', content: 'Request failed to reach the server.', error: msg, timestamp: Date.now(),
      }]));
      toast({ status: 'error', title: 'AI modify failed', description: msg });
      setBusy(false);
    }
    // buildRequestBody / extractResult / onStateApplied are read via ref
    // (see refs above) so we intentionally exclude them from deps —
    // otherwise send would re-memoize every parent render and drop a
    // running poll on the floor.
  }, [input, busy, history, modifyUrl, currentState, toast]); // eslint-disable-line react-hooks/exhaustive-deps

  const pollJob = useCallback(async (jobId: string): Promise<void> => {
    const startedAt = Date.now();
    return new Promise<void>((resolve) => {
      const tick = async () => {
        try {
          const s = await apiJson<ModifyStatusResponse>(modifyStatusUrl(jobId));
          if (s.status === 'done') {
            const { state, summary } = extractResultRef.current(s);
            if (state != null) {
              const displaySummary = (summary && summary.trim()) || 'Applied your requested changes.';
              setHistory(prev => trimForStorage([...prev, {
                role: 'assistant', content: displaySummary, snapshot: state, timestamp: Date.now(),
              }]));
              onStateAppliedRef.current(state);
              setBusy(false);
              resolve();
              return;
            }
            // done with no state — treat as failure so the operator
            // gets a visible reply instead of a silent no-op.
            setHistory(prev => trimForStorage([...prev, {
              role: 'assistant', content: 'The model returned no usable result.', error: 'empty response', timestamp: Date.now(),
            }]));
            setBusy(false);
            resolve();
            return;
          }
          if (s.status === 'failed') {
            const msg = s.error || 'Unknown error';
            setHistory(prev => trimForStorage([...prev, {
              role: 'assistant', content: 'The model couldn\'t apply that change.', error: msg, timestamp: Date.now(),
            }]));
            toast({ status: 'error', title: 'AI modify failed', description: msg });
            setBusy(false);
            resolve();
            return;
          }
        } catch { /* transient — retry on next tick */ }
        if (Date.now() - startedAt > TIMEOUT_MS) {
          setHistory(prev => trimForStorage([...prev, {
            role: 'assistant', content: 'Timed out waiting on the model. Try again with a simpler request.',
            error: 'timeout', timestamp: Date.now(),
          }]));
          toast({ status: 'warning', title: 'Still working', description: 'Try again shortly.' });
          setBusy(false);
          resolve();
          return;
        }
        setTimeout(tick, INTERVAL_MS);
      };
      setTimeout(tick, INTERVAL_MS);
    });
  }, [modifyStatusUrl, toast]); // eslint-disable-line react-hooks/exhaustive-deps

  const revertToTurn = useCallback((idx: number) => {
    const t = history[idx];
    if (!t || t.role !== 'assistant' || t.snapshot === undefined) return;
    onStateAppliedRef.current(t.snapshot);
    setHistory(prev => trimForStorage([...prev, {
      role: 'assistant',
      content: `Reverted to the state after "${truncate(history[idx - 1]?.content, 60)}"`,
      snapshot: t.snapshot,
      timestamp: Date.now(),
    }]));
  }, [history]);

  const clearHistory = useCallback(() => {
    setHistory([]);
    saveHistory(storageKey, []);
  }, [storageKey]);

  const stats = useMemo(() => {
    const users = history.filter(t => t.role === 'user').length;
    return { turns: users };
  }, [history]);

  return (
    <VStack align="stretch" spacing={2}>
      <HStack justify="space-between" px={1}>
        <HStack spacing={2}>
          <Text fontSize="xs" color="brand.muted">
            {history.length === 0 ? 'No conversation yet — describe a change to start.' : `${stats.turns} exchange${stats.turns === 1 ? '' : 's'} in this thread`}
          </Text>
        </HStack>
        {history.length > 0 && (
          <Button size="xs" variant="ghost" onClick={clearHistory} isDisabled={busy}>
            Clear
          </Button>
        )}
      </HStack>

      {/* Message log */}
      <Box
        ref={scrollRef}
        borderWidth="1px"
        borderColor="brand.border"
        borderRadius="md"
        bg="brand.canvas"
        p={2}
        maxH="320px"
        minH="120px"
        overflowY="auto"
      >
        {history.length === 0 ? (
          <VStack py={4} spacing={1}>
            <Text fontSize="xs" color="brand.muted" textAlign="center">
              Ask the model to change anything.
            </Text>
            <Text fontSize="2xs" color="brand.muted" textAlign="center" fontStyle="italic">
              {emptyStateExample}
            </Text>
          </VStack>
        ) : (
          <VStack align="stretch" spacing={2}>
            {history.map((t, i) => (
              <ChatBubble
                key={`${t.timestamp}-${i}`}
                turn={t}
                onRevert={t.role === 'assistant' && t.snapshot !== undefined ? () => revertToTurn(i) : undefined}
              />
            ))}
            {busy && (
              <HStack spacing={2} px={2} py={1} color="brand.muted">
                <Spinner size="xs" />
                <Text fontSize="xs">Thinking…</Text>
              </HStack>
            )}
          </VStack>
        )}
      </Box>

      {/* Input row */}
      <HStack spacing={2} align="flex-end">
        <Textarea
          ref={inputRef}
          size="sm"
          rows={2}
          resize="none"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            // Enter sends; Shift+Enter inserts a newline.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          placeholder={placeholder}
          isDisabled={busy}
        />
        <Button
          size="sm"
          variant="brand"
          onClick={() => void send()}
          isLoading={busy}
          loadingText="…"
          isDisabled={!input.trim()}
          flexShrink={0}
          alignSelf="stretch"
        >
          Send
        </Button>
      </HStack>
    </VStack>
  );
}

// ── Chat bubble ────────────────────────────────────────────────────

function ChatBubble<TState>({ turn, onRevert }: { turn: ChatTurn<TState>; onRevert?: () => void }) {
  const isUser = turn.role === 'user';
  const isError = turn.role === 'assistant' && !!turn.error;
  return (
    <HStack justify={isUser ? 'flex-end' : 'flex-start'} align="flex-start" spacing={2}>
      {!isUser && (
        <Box
          w="24px" h="24px"
          borderRadius="full"
          bg={isError ? 'red.100' : 'purple.100'}
          color={isError ? 'red.700' : 'purple.700'}
          display="flex" alignItems="center" justifyContent="center"
          fontSize="10px" fontWeight="700"
          flexShrink={0}
        >
          {isError ? '!' : 'AI'}
        </Box>
      )}
      <Box maxW="82%">
        <Box
          px={3} py={2}
          borderRadius="lg"
          bg={isUser ? 'purple.500' : isError ? 'red.50' : 'white'}
          color={isUser ? 'white' : isError ? 'red.900' : 'brand.text'}
          borderWidth={isUser ? 0 : '1px'}
          borderColor={isError ? 'red.200' : 'brand.border'}
          fontSize="xs"
          whiteSpace="pre-wrap"
        >
          {turn.content}
          {turn.role === 'assistant' && turn.error && (
            <Text fontSize="2xs" color="red.700" mt={1} fontFamily="mono">
              {turn.error}
            </Text>
          )}
        </Box>
        {onRevert && (
          <HStack mt={1} spacing={2} px={1}>
            <Badge fontSize="2xs" colorScheme="purple" variant="subtle">Applied</Badge>
            <IconButton
              size="xs" variant="ghost" aria-label="Revert to this state"
              onClick={onRevert}
              icon={<span style={{ fontSize: '11px' }}>↶ Revert</span>}
              w="auto" px={1.5}
            />
          </HStack>
        )}
      </Box>
      {isUser && (
        <Box
          w="24px" h="24px"
          borderRadius="full"
          bg="brand.border"
          color="brand.text"
          display="flex" alignItems="center" justifyContent="center"
          fontSize="10px" fontWeight="700"
          flexShrink={0}
        >
          You
        </Box>
      )}
    </HStack>
  );
}

// ── Persistence ────────────────────────────────────────────────────
//
// Callers pass a fully-qualified storage key (e.g. `titleStudio.brand.
// <id>.<format>` or `videoScript.brand.<id>.<format>`), so the panel
// doesn't need to prefix it — the caller owns the namespace. Prevents
// two chats using the same panel from colliding on a shared prefix.

function loadHistory<TState>(key: string): ChatTurn<TState>[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((t): t is ChatTurn<TState> => {
      return t && typeof t === 'object' && (t.role === 'user' || t.role === 'assistant') && typeof t.content === 'string';
    });
  } catch { return []; }
}

function saveHistory<TState>(key: string, history: ChatTurn<TState>[]) {
  try {
    localStorage.setItem(key, JSON.stringify(trimForStorage(history)));
  } catch {
    // localStorage full or blocked — silently drop. History still lives
    // in React state for the current session.
  }
}

// Cap total stored turns so a very long thread doesn't blow past the
// browser's per-key quota (typically ~5 MB). Older turns fall off the
// front; the most-recent MAX_TURNS_STORED survive.
function trimForStorage<TState>(history: ChatTurn<TState>[]): ChatTurn<TState>[] {
  if (history.length <= MAX_TURNS_STORED) return history;
  return history.slice(history.length - MAX_TURNS_STORED);
}

function truncate(s: string | undefined, n: number): string {
  if (!s) return '';
  return s.length <= n ? s : s.slice(0, n - 1) + '…';
}
